import { computed, inject } from 'vue'
import { DISCOUNT_TYPES, PAYMENT_SOURCE_TOKEN } from '@/constants/payment.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device.js'
import { useNotification } from '@/composables/useNotification.js'
import { usePayment } from '@/composables/usePayment'
import { usePackageDeal } from '@/composables/usePackageDeal'
import { usePaymentStore } from '@/stores/payment.js'
import { useCartStore } from '@/stores/cart.js'
import { getErrorMessage } from '@/utils/error.js'
import { useUiStore } from '@/stores/ui.js'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useCartValidation } from '@/composables/useCartValidation.js'
import { useCustomer } from '@/composables/useCustomer.js'

export function useCart() {
  const cl = inject('cl')
  const clUtils = inject('clUtils')
  const Sentry = inject('Sentry')
  const uiStore = useUiStore()
  const customerStore = useCustomerStore()
  const deviceStore = useDeviceStore()
  const paymentStore = usePaymentStore()
  const cartStore = useCartStore()
  const { showNotification } = useNotification()
  const { terminalTransactionVerify, terminalTransactionAbort, terminalPaymentInProgress } =
    usePayment()
  const { cartValidate } = useCartValidation()
  const { loadCustomerById } = useCustomer()
  const {
    getMode,
    packageDealApplied,
    removePackageDeal,
    cleanPackageDealOnOrderCopy,
    freezePackageDealLinkOnApprove,
    getCustomerLastOrder
  } = usePackageDeal()
  const currentCart = computed(() => customerStore.getCurrentCart)
  const currentStoreReference = computed(() => deviceStore.settings.warehouse)
  const marketId = computed(() => deviceStore.getMarketId)
  const { t } = useI18n()
  const router = useRouter()

  const cartLineItems = computed(() => {
    const data = currentCart.value?.line_items
    const items = data?.filter((lineItem) => {
      // if metadata.type is rounding don't return it.
      if (lineItem.metadata?.type === 'rounding') {
        return false
      }
      if (lineItem.metadata?.type === 'insurance') {
        return !lineItem.reference
      } else if (['gift_card', 'insurance'].includes(lineItem.metadata?.type)) {
        return true // Include gift cards and insurance regardless of reference
      } else if (['shipments', 'payment_methods', ...DISCOUNT_TYPES].includes(lineItem.item_type)) {
        return false
      } else if (
        lineItem.item?.metadata?.type === 'lens' ||
        lineItem.item?.metadata?.type === 'accessory'
      ) {
        return !lineItem.reference // Include lenses only if no reference
      } else {
        return true
      }
    })
    return items
  })

  const cartCaseLineItems = computed(() => {
    const data = currentCart.value?.line_items

    const items = data?.filter((lineItem) => {
      if (
        lineItem.item?.metadata?.type === 'accessory' &&
        lineItem.item.metadata?.sub_type === 'Case' &&
        lineItem.reference
      ) {
        return lineItem
      }
    })
    return items
  })

  const cartHasInsuranceTag = computed(() => {
    //check if currentCart.tags has a item where name is insurance.
    return currentCart.value?.tags?.some((tag) => tag.name === 'insurance')
  })

  const cartHasAfterSalesGiftCard = computed(() => {
    return currentCart.value?.attachments?.some((item) => {
      return (
        item.metadata.gift_card_reference?.startsWith('AS/INSURANCE') ||
        item.metadata.gift_card_reference?.startsWith('AS/INTOLERANCE')
      )
    })
  })

  const cartHasAfterSalesIntoleranceGiftCard = computed(() => {
    return currentCart.value?.attachments?.some((item) => {
      return item.metadata.gift_card_reference?.startsWith('AS/INTOLERANCE')
    })
  })

  const cartHasAfterSalesInsuranceGiftCard = computed(() => {
    const foundItem = currentCart.value?.attachments?.find((item) => {
      return item.metadata.gift_card_reference?.startsWith('AS/INSURANCE')
    })

    return foundItem ? foundItem.metadata.gift_card_reference : false
  })

  const cartHasIntoleranceTag = computed(() => {
    //check if currentCart.tags has a item where name is intolerance.
    return currentCart.value?.tags?.some((tag) => tag.name === 'intolerance')
  })

  const cartDeductibleAmount = computed(() => {
    if (!cartHasAfterSalesInsuranceGiftCard.value) {
      return false
    }

    const foundItem = currentCart.value?.attachments?.find((item) => {
      return item.metadata.gift_card_reference?.startsWith('AS/INSURANCE')
    })

    return foundItem ? foundItem.metadata.min_payable_amount : false
  })

  const cartHasGiftCardWithMinimumAmount = computed(() => {
    return currentCart.value?.attachments?.some((item) => {
      return item.metadata.min_payable_amount
    })
  })

  const expectedDeliverySlips = computed(() => {
    // return items from cart where directHandover is true and reference is set OR if lens
    return currentCart.value?.line_items?.filter(
      (item) =>
        (!item?.metadata?.immediate_handover &&
          item?.reference &&
          item?.metadata?.origin_stock_location === currentStoreReference.value) ||
        item?.metadata?.type === 'lens'
    )
  })

  const cartHasFrameOrLensSparePart = computed(() => {
    // return true if any item in cartLineItems has property metadata.type frame or sun or customMadeFrame.
    if (cartLineItems.value) {
      return cartLineItems.value.some((item) => {
        return ['frame', 'sun', 'customMadeFrame', 'lens'].includes(item.item?.metadata?.type)
      })
    }

    return false
  })

  const isHandedOverDirectly = computed(() =>
    cartLineItems?.value?.every((lineItem) => lineItem.metadata?.immediate_handover === true)
  )

  const allGlassesAreHandedOverDirectly = computed(() => {
    if (cartLineItems.value) {
      const glasses = cartLineItems.value.filter((item) =>
        ['sun', 'frame'].includes(item.metadata?.type)
      )
      if (glasses.length === 0) {
        return false
      }

      return glasses.every((item) => {
        return item.metadata?.immediate_handover
      })
    }
    return false
  })

  const hasAnyHandedOverItem = computed(() =>
    cartLineItems?.value?.some((lineItem) => lineItem.metadata?.immediate_handover === true)
  )

  function getAfterSalesGiftcard(order) {
    // if order has an attachment with gift_card_reference starting with AS/INSURANCE or AS/INTOLERANCE return the gift card metadata.origin_order_id.
    const foundItem = order.attachments.find((item) => {
      return (
        item.metadata.gift_card_reference?.startsWith('AS/INSURANCE') ||
        item.metadata.gift_card_reference?.startsWith('AS/INTOLERANCE')
      )
    })
    return foundItem
      ? {
          order_id: foundItem.metadata?.origin_order_id,
          giftcard_type: foundItem.metadata.gift_card_reference?.startsWith('AS/INSURANCE')
            ? 'insurance'
            : 'intolerance'
        }
      : false
  }

  async function createCart(type = 'order') {
    // if customer starts with vcp and ends with shopviu.com reset the customerStore
    if (
      customerStore.customer?.email &&
      customerStore.customer.email.startsWith('vcp') &&
      customerStore.customer.email.endsWith('shopviu.com')
    ) {
      customerStore.reset()
    }

    const data = {
      market: cl.markets.relationship(marketId.value),
      ...(customerStore?.customer?.id && {
        customer: cl.customers.relationship(customerStore.customer.id)
      }),
      metadata: {
        employee_created: {
          eid: deviceStore.settings.currentEmployee.eid,
          name: deviceStore.settings.currentEmployee.name
        },
        card_source_channel: 'wings',
        card_source_type: type
      },
      language_code: customerStore.customer?.metadata?.language
        ? customerStore.customer.metadata.language.toLowerCase()
        : deviceStore.settings.language.toLowerCase()
    }
    try {
      const cart = await cl.orders.create(data, {
        include: ['market']
      })

      customerStore.setCurrentCart(cart)
      return cart
    } catch (error) {
      Sentry.setContext('createCart', {
        data: data
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('cartCreate')
        return scope
      })
      showNotification({
        message: 'Could not create new cart.<br />Error: ' + error,
        variant: 'danger'
      })
    }
  }

  function resetCartState() {
    customerStore.reset()
    cartStore.shippingAddressSetOnCart = false
    cartStore.customerSetOnCart = false
    uiStore.showCart = false
  }

  async function archiveCart() {
    const order = await cl.orders.retrieve(currentCart.value.id, {
      fields: { orders: ['status'] }
    })
    if (['draft', 'pending', 'editing', 'placed'].includes(order.status)) {
      try {
        // if editing call _stop_editing first
        if (order.status === 'editing') {
          await cl.orders.update({
            id: currentCart.value.id,
            _stop_editing: true
          })
        }

        await cl.orders.update({
          id: currentCart.value.id,
          _archive: true
        })
      } catch (error) {
        showNotification({
          message: 'Could not remove the cart.<br />Error: ' + error,
          variant: 'danger'
        })

        return false
      }
    }

    resetCartState()
  }

  function getPackageDealMode(currentCart) {
    const hasPackageDealApplied = packageDealApplied(currentCart)
    if (hasPackageDealApplied) {
      const mode = getMode(currentCart)

      // if mode contains _ only the part before _
      if (mode && mode.includes('_')) {
        return mode.split('_')[0]
      }
      return mode
    }
    return null
  }

  // Not ideal but we need to check if package deal is applied and if mode has changed on cart update.
  // If so, remove package deal from cart and notify user.
  // This is to prevent issues with package deal being applied on incompatible items.
  async function removePackageDealOnCartChange(modeBefore, cartAfter) {
    const isPackageDealApplied = packageDealApplied(cartAfter)
    const modeAfter = getPackageDealMode(cartAfter)

    if (isPackageDealApplied && modeBefore !== null && modeAfter !== modeBefore) {
      showNotification({
        message: t('package_deal.notification.removed_on_cart_change'),
        variant: 'info'
      })
      await removePackageDeal(cartAfter)
      await cartUpdate(null, true)
    }
  }

  async function cartUpdate(update = null, skipPackageDealCheck = false) {
    if (!currentCart.value?.id) return
    const modeBefore = getPackageDealMode(currentCart.value)
    try {
      var options = {
        include: [
          'authorizations.captures',
          'attachments',
          'line_items.item',
          'line_items.line_item_options.sku_option',
          'market',
          'shipping_address',
          'billing_address',
          'tags'
        ],
        fields: {
          markets: ['code', 'metadata'],
          orders: [
            'authorizations',
            'attachments',
            'subtotal_amount_float',
            'total_amount_float',
            'currency_code',
            'coupon_code',
            'fulfillment_status',
            'payment_status',
            'status',
            'number',
            'created_at',
            'shipping_address',
            'billing_address',
            'metadata',
            'line_items',
            'market',
            'tags',
            'skus_count',
            'gift_card_code',
            'total_amount_with_taxes_float'
          ],
          authorizations: ['amount_cents', 'captures', 'number']
        }
      }
      if (update) {
        const cart = await cl.orders.update(
          {
            id: currentCart.value.id,
            ...update
          },
          options
        )

        customerStore.setCurrentCart(cart)
      } else {
        const cart = await cl.orders.retrieve(currentCart.value.id, options)
        customerStore.setCurrentCart(cart)
      }

      if (!skipPackageDealCheck) {
        await removePackageDealOnCartChange(modeBefore, currentCart.value)
      }
    } catch (error) {
      showNotification({
        message: 'cartUpdate:' + getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function deleteLineItem(id, ref, skipAttachements = false) {
    // get current order fresh from CL. and check if status is draft or pending. if so, exit the function.
    const order = await cl.orders.retrieve(currentCart.value.id, {
      fields: { orders: ['status'] }
    })
    if (!['draft', 'pending'].includes(order.status)) {
      showNotification({
        message: t('cart.cartIsNotDraftOrPending'),
        variant: 'danger'
      })
      return
    }

    try {
      await cl.line_items.delete(id)
      await cartUpdate()

      if (ref !== null && customerStore.customer.currentCart.line_items.length > 0) {
        const attachedLineItems = customerStore.customer.currentCart.line_items.filter(
          (item) => item.reference === ref
        )

        if (attachedLineItems.length > 0 && skipAttachements === false) {
          for (const item of attachedLineItems) {
            await cl.line_items.delete(item.id)
          }
        }
        await cartUpdate()
      }
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function removeInsurance(ref) {
    try {
      const insurance = customerStore.customer.currentCart.line_items.filter((item) => {
        return item.reference === ref && item.sku_code === 'INSURANCE'
      })

      const frame = customerStore.customer.currentCart.line_items.filter((item) => {
        return item.reference === ref && ['frame', 'customMadeFrame'].includes(item.metadata.type)
      })

      if (frame) {
        delete frame[0].metadata.insurance
        const attributes = {
          id: frame[0].id,
          metadata: {
            ...frame[0].metadata
          }
        }

        await cl.line_items.update(attributes)
      }

      await cl.line_items.delete(insurance[0].id)
      await cartUpdate()
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function addLineItem(item, skipCheck = false) {
    if (skipCheck === false) {
      if (cartHasAfterSalesGiftCard.value && item.sku_code !== 'SPARE20') {
        showNotification({
          message: t('afterSales.cartRestricted'),
          variant: 'danger',
          icon: 'exclamation-octagon',
          duration: 6000
        })
        return
      }
    }

    if (cartHasAfterSalesGiftCard.value && cartHasFrameOrLensSparePart.value) {
      showNotification({
        message: t('afterSales.cartRestrictedNoMoreItems'),
        variant: 'danger',
        icon: 'exclamation-octagon',
        duration: 6000
      })
      return
    }

    try {
      return await cl.line_items.create({
        ...item,
        order: {
          type: 'orders',
          id: currentCart.value.id
        }
      })
    } catch (error) {
      let name = item.sku_code

      // try to fetch sku name
      try {
        const sku = await cl.skus.list({
          filters: {
            code_eq: item.sku_code
          },
          fields: { skus: ['metadata'] }
        })
        name = sku[0]?.metadata?.title[deviceStore.settings.language] || item.sku_code
      } catch (error) {
        console.error('Error fetching SKU name:', error)
      }

      Sentry.setContext('addLineItem', {
        item: item,
        currentCartMarket: currentCart.value?.market
      })

      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('addLineItem')
        scope.setTag('order_number', currentCart.value?.number)
        return scope
      })
      const message = getErrorMessage(error)
      showNotification({
        message: t('validation.lineItems.apiError.helpText', {
          name: name,
          errorMessage: message
        }),
        variant: 'danger',
        icon: 'exclamation-octagon',
        duration: 10000
      })

      return false
    }
  }

  // If it’s not a frame and not a customMadeFrame and the article comes from the warehouse of the local store
  // immediate_handover is set to true
  async function setImmediateHandoverOnLineItem(lineItemData) {
    try {
      const data = {
        id: lineItemData.id,
        metadata: {
          ...lineItemData.metadata
        }
      }

      const lineItem = currentCart.value?.line_items.find((item) => item.id === lineItemData.id)

      /* if immediate_handover ist already true -> return */
      if (
        lineItem.metadata.immediate_handover === lineItemData.metadata.immediate_handover &&
        lineItem.metadata.immediate_handover !== undefined
      ) {
        return
      }

      if (
        !['frame', 'customMadeFrame', 'lens'].includes(lineItem?.item.metadata?.type) &&
        lineItem?.metadata?.origin_stock_location === currentStoreReference.value &&
        deviceStore.settings.stockLocationId !== deviceStore.settings.serviceHubId
      ) {
        data.metadata.immediate_handover = true

        await cl.line_items.update(data)
        const foundLineItem = currentCart.value.line_items?.find(
          (lineItem) => lineItem.id === lineItemData.id
        )
        if (foundLineItem) {
          foundLineItem.metadata.immediate_handover = data.metadata.immediate_handover
        }
      }
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  function hasRoundingAdjustment() {
    const roundingAdjustments = currentCart.value?.line_items?.filter(
      (item) => item.metadata?.type === 'rounding'
    )

    return roundingAdjustments?.length === 0 ? false : roundingAdjustments
  }

  async function addAdjustmentAmount(amount) {
    if (hasRoundingAdjustment()) {
      return
    }
    try {
      const adjustment = await cl.adjustments.create({
        type: 'ROUNDING',
        name: 'ROUNDING',
        currency_code: currentCart.value.currency_code,
        amount_cents: parseInt(amount * 100),
        distribute_discount: false,
        metadata: {
          title: {
            de: 'Rundung',
            en: 'Rounding',
            fr: 'Arrondi'
          }
        }
      })

      if (adjustment.id) {
        addLineItem({
          sku_code: 'ROUNDING',
          quantity: 1,
          item: cl.adjustments.relationship(adjustment.id),
          metadata: {
            type: 'rounding',
            adjustment_id: adjustment.id
          }
        })
      }

      await cartUpdate()
    } catch (error) {
      const message = getErrorMessage(error)
      showNotification({
        message: `Adjustment could not be added to the cart.<br /><b>Error:</b> ${message}`,
        variant: 'danger',
        icon: 'exclamation-octagon',
        duration: 6000
      })

      return false
    }
  }

  async function addGiftcardAttachmentToOrder(giftCard, cartId = false) {
    if (
      giftCard.expires_at &&
      new Date(giftCard.expires_at) < new Date() &&
      giftCard.expires_at !== null
    ) {
      showNotification({
        message: t('giftCard.expired'),
        variant: 'danger'
      })
      return false
    }

    const addedGiftcard = await cl.attachments.create({
      name: 'Giftcard',
      reference: giftCard.code,
      reference_origin: 'payment--gift_card',
      metadata: {
        balance_float: giftCard.balance_float,
        balance_cents: giftCard.balance_float * 100,
        gift_card_reference_origin: giftCard.reference_origin,
        gift_card_reference: giftCard.reference,
        ...(giftCard.metadata?.min_payable_amount && {
          min_payable_amount: giftCard.metadata.min_payable_amount
        }),
        ...(giftCard.metadata?.origin_return_number && {
          origin_return_number: giftCard.metadata.origin_return_number
        }),
        ...(giftCard.metadata?.origin_order_id && {
          origin_order_id: giftCard.metadata.origin_order_id
        }),
        ...(giftCard.metadata?.gift_card_type && {
          gift_card_type: giftCard.metadata.gift_card_type
        })
      },
      attachable: cl.orders.relationship(cartId ? cartId : currentCart.value.id)
    })

    if (addedGiftcard.id) {
      // if cartId is passed it means it's not checkout. so no need to update the card
      if (!cartId) {
        showNotification({
          icon: 'gift',
          message: t('giftCard.addedSuccessfully'),
          variant: 'success'
        })
      }
      await cartUpdate()
      return addedGiftcard
    } else {
      showNotification({
        icon: 'exclamation-triangle',
        message: t('giftCard.notAddedToOrder'),
        variant: 'warning'
      })
      return false
    }
  }

  async function removeGiftcardAttachmentFromOrder(giftcardId) {
    const removedGiftcard = await cl.attachments.delete(giftcardId)
    if (!removedGiftcard) {
      showNotification({
        icon: 'trash',
        message: t('giftCard.removedSuccessfully'),
        variant: 'success'
      })
      await cartUpdate()
      return true
    } else {
      showNotification({
        icon: 'exclamation-triangle',
        message: t('giftCard.notRemovedFromOrder'),
        variant: 'danger'
      })
      return false
    }
  }

  async function getGiftCard(giftCardCode) {
    return await cl.gift_cards.list({
      include: ['market'],
      filters: {
        code_eq: giftCardCode
      },
      pageSize: 1
    })
  }

  async function getGiftcardByCodeForOrder(code, email, currency, silent = false) {
    let giftCard = null
    let data = await getGiftCard(code)

    if (!data || data.length < 1) {
      // for legacy code e.g. employee types only VIUGC1234 try it with currency prefix
      data = await getGiftCard(`${currency}-${code}`)
    }

    if (data && data.length > 0) {
      giftCard = data[0]
    } else {
      if (!silent) {
        showNotification({
          message: 'Could not find gift card with code ' + code + ' for email ' + email,
          variant: 'danger'
        })
      }
      return false
    }

    if (giftCard.status !== 'active') {
      showNotification({
        message: t('giftCard.isNotActive'),
        variant: 'danger'
      })
      return false
    } else if (giftCard.balance_cents == 0) {
      showNotification({
        message: t('giftCard.noBalance'),
        variant: 'danger'
      })
      return false
    } else if (giftCard.currency_code !== deviceStore.settings.currency) {
      showNotification({
        message: t('giftCard.currencyMismatch'),
        variant: 'danger'
      })
      return false
    }

    return giftCard
  }

  function getCurrentItemReference() {
    if (!currentCart.value || !currentCart.value?.line_items) {
      return '001'
    } else {
      // get all line_items from currentCart where reference is not null and return the highest number
      let highestNumber = 0
      for (const item of currentCart.value.line_items) {
        if (item.reference && parseInt(item.reference) > highestNumber) {
          highestNumber = parseInt(item.reference)
        }
      }
      return (highestNumber + 1).toString().padStart(3, '0')
    }
  }

  function orderQuery() {
    return {
      include: [
        'attachments',
        'line_items.item',
        'line_items.line_item_options.sku_option',
        'market',
        'shipping_address',
        'billing_address',
        'shipments.available_shipping_methods',
        'tags',
        'authorizations',
        'captures'
      ],
      fields: {
        markets: ['code', 'metadata'],
        orders: [
          'attachments',
          'subtotal_amount_float',
          'total_amount_float',
          'currency_code',
          'fulfillment_status',
          'payment_status',
          'status',
          'number',
          'created_at',
          'shipping_address',
          'billing_address',
          'metadata',
          'line_items',
          'market',
          'tags',
          'skus_count',
          'shipments',
          'gift_card_code',
          'total_amount_with_taxes_float',
          'authorizations',
          'captures'
        ]
      }
    }
  }

  async function hasInProgressPayment(currentOrder, refreshCurrentOrder = false) {
    if (refreshCurrentOrder) {
      currentOrder = await cl.orders.retrieve(currentOrder.id, orderQuery())
    }

    const terminalPayment = currentOrder.authorizations?.find(
      (auth) => auth.reference_origin === 'CREDIT_CARD' && auth.reference
    )

    // and current order is in state editing or pending or placed
    if (['editing', 'pending', 'placed'].includes(currentOrder.status) && terminalPayment) {
      const inProgressPayment = currentOrder?.captures?.find(terminalPaymentInProgress)
      return inProgressPayment ? true : false
    }

    return false
  }

  /**
   * Verify terminal payment
   *
   * - If the payment is in progress, abort the transaction
   * - Then Adyen Terminal via Supabase function to verify the payment -> if the user was too quick
   * - If the payment is successful, the order is fetched again to get the updated status
   * - Use this to provide order copies with already captured payments
   */
  async function verifyTerminalPayment(currentOrder) {
    // first check clean up terminal transactions
    // if terminal payment and captures has status succeeded = false and message = 'Accepted' then verify adyen payment
    const terminalPayment = currentOrder.authorizations?.find(
      (auth) => auth.reference_origin === 'CREDIT_CARD' && auth.reference
    )

    if (terminalPayment) {
      const inProgressPayment = currentOrder?.captures?.find(terminalPaymentInProgress)
      // abort current transaction
      if (inProgressPayment) {
        await terminalTransactionAbort(inProgressPayment, terminalPayment.reference)

        // now check if the payment is already successful -> if the user was too quick
        const result = await terminalTransactionVerify(inProgressPayment, terminalPayment.reference)

        if (result?.data?.success) {
          // wait for 5s and fetch the order again - wait till the supabase webhook has updated the order
          await new Promise((resolve) => setTimeout(resolve, 5000))
          // fetch the order again to
          return await cl.orders.retrieve(currentOrder?.id, orderQuery)
        }
      }
    }

    return currentOrder
  }

  async function showOrderSuccessNotification(currentOrder) {
    uiStore.showCartPaymentDialog = false
    uiStore.showCart = false
    paymentStore.$patch({
      payment: {
        orderId: currentOrder.id,
        orderNumber: currentOrder.number
      },
      expectedDeliverySlips: expectedDeliverySlips?.value.length
    })
    resetCartState()
    uiStore.orderUpdateOverlay = false
    await router.push({ name: 'order-success' })
  }

  async function handleApprove(currentOrder) {
    try {
      if (currentOrder.status === 'editing') {
        currentOrder = await cl.orders.update({
          id: currentOrder.id,
          _stop_editing: true
        })
      }

      // if order already approved, just fetch it again
      if (currentOrder.status === 'approved') {
        currentOrder = await cl.orders.retrieve(currentOrder.id, orderQuery())
      } else {
        // if order is not approved, we need to approve it
        currentOrder = await cl.orders.update(
          {
            id: currentOrder.id,
            _approve: true
          },
          orderQuery()
        )
      }

      await freezePackageDealLinkOnApprove(currentOrder)
      return await showOrderSuccessNotification(currentOrder)
    } catch (error) {
      Sentry.setContext('data', {
        orderId: currentOrder.id,
        currentCart: currentOrder
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('handleApprove')
        scope.setTag('order_number', currentOrder?.number)
        scope.setTag('wings_process', 'order_approve')
        return scope
      })
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  /**
   * Handle cart states on error or order copy
   *
   * - if the order is in draft or pending state, we don't need to copy it
   * - if the order is already approved, we don't need to copy it
   * - if the order is already paid or partially paid, we don't need to copy it - just approve it
   * - if the order is in editing state, we need to stop editing it.
   *
   * Else create a copy of the order.
   *
   * @param {*} order
   * @returns
   */
  async function handleCartErrorStates(currentOrder, doCopyOrder = false) {
    try {
      if (!currentOrder?.id) throw new Error('No current order provided')
      currentOrder = await cl.orders.retrieve(currentOrder?.id, orderQuery())
      currentOrder = await verifyTerminalPayment(currentOrder)
      const inProgressPayment = await hasInProgressPayment(currentOrder, false)

      if (['draft', 'pending'].includes(currentOrder?.status)) {
        // if the order is still in draft or pending status, do nothing order is still editable
        uiStore.orderUpdateOverlay = false
        customerStore.setCurrentCart(currentOrder)
        return currentOrder
      } else if (['approved'].includes(currentOrder?.status)) {
        // if the order is already approved, we don't need to copy it
        return await showOrderSuccessNotification(currentOrder)
      } else if (['paid', 'partially_paid'].includes(currentOrder?.payment_status)) {
        return await handleApprove(currentOrder)
      } else if (inProgressPayment) {
        uiStore.orderUpdateOverlay = false
        customerStore.setCurrentCart(currentOrder)
        uiStore.showCartPaymentOnOtherClientInProgressDialog = true
        return currentOrder
      } else if (doCopyOrder) {
        customerStore.setCurrentCart(currentOrder)
        return await createOrderCopy(currentOrder)
      } else {
        uiStore.orderUpdateOverlay = false
        customerStore.setCurrentCart(currentOrder)
        return currentOrder
      }
    } catch (error) {
      Sentry.setContext('orderCopy', {
        order: currentOrder
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('handleCartPlaceErrors')
        scope.setTag('order_number', currentOrder?.number)
        scope.setTag('wings_process', 'order_approve')
        return scope
      })
      uiStore.orderUpdateOverlay = false
      return null
    }
  }

  /**
   * Wrapper function to handle cart place errors (old order copy)
   *
   * @param {*} order
   */
  async function handleCartPlaceErrors(order, doCopyOrder = false) {
    let currentOrder = order
    uiStore.orderUpdateOverlay = true
    uiStore.showCartPaymentDialog = false // for safety hide the payment dialog
    // wait 2s before creating the order copy -> to avoid rate limiting (maybe also some fetch failed)
    await new Promise((resolve) => setTimeout(resolve, 2000))

    return await handleCartErrorStates(currentOrder, doCopyOrder)
  }

  async function putCartToEditing(currentOrder) {
    if (currentOrder.status === 'placed') {
      return await cl.orders.update(
        {
          id: currentOrder.id,
          _start_editing: true
        },
        orderQuery()
      )
    }

    return currentOrder
  }

  async function processOrderCopyCompleted(targetOrder, sourceOrder) {
    const attachmentsToCopy = sourceOrder?.attachments || []
    // for each order.attachment update the reference to the new order
    for (const attachment of attachmentsToCopy) {
      await cl.attachments.update({
        id: attachment.id,
        attachable: cl.orders.relationship(targetOrder.id)
      })
      // wait for 1s to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    await cleanPackageDealOnOrderCopy(targetOrder.id, sourceOrder, attachmentsToCopy)

    // set _refresh to true to avoid the discount calculation issue
    return await cl.orders.update(
      {
        id: targetOrder.id,
        _refresh: true
      },
      orderQuery()
    )
  }

  /**
   * Use this function to manually copy the order
   *
   * @param {*} currentOrder
   * @returns
   */
  async function manuallyCopyOrder() {
    uiStore.orderUpdateOverlay = true
    return await handleCartErrorStates(currentCart.value, true)
  }

  async function createOrderCopy(currentOrder) {
    let orderCopyCompleted = null
    try {
      // Check stock availability before creating the order copy
      try {
        // delete all order reservations before creating an order copy
        const stockReservations = await clUtils.retrieveAll('stock_reservations', {
          filters: {
            reference_or_order_number_eq: currentOrder.number
          }
        })

        // create a task for each stock reservations
        const tasks = []
        for (const reservation of stockReservations) {
          tasks.push({
            resourceType: 'stock_reservations',
            operation: 'delete',
            resource: reservation.id
          })
        }

        await clUtils.executeBatch({
          tasks: tasks
        })

        // validate Cart before Copy
        const cardValid = await cartValidate()

        // if not valid don't create copy - and put order to editing
        if (!cardValid) {
          uiStore.orderUpdateOverlay = false
          currentOrder = await putCartToEditing(currentOrder)
          customerStore.setCurrentCart(currentOrder)
          return
        }
      } catch (error) {
        Sentry.setContext('orderCopy', {
          order: currentOrder
        })
        Sentry.captureException(error, (scope) => {
          scope.setTransactionName('handleOrderCopyCheckStock')
          scope.setTag('order_number', currentOrder?.number)
          scope.setTag('wings_process', 'order_approve')
          return scope
        })
      }

      // The source order can only be canceled if it is in the 'placed' status
      const orderCopy = await cl.order_copies.create({
        source_order: currentOrder,
        cancel_source_order: ['placed'].includes(currentOrder.status),
        apply_promotions: true,
        ignore_invalid_coupon: true
      })

      let counter = 0
      while (counter < 120) {
        orderCopyCompleted = await isOrderCopyCompleted(orderCopy.id)
        await new Promise((resolve) => setTimeout(resolve, 1000))
        if (orderCopyCompleted) break
        counter++
      }

      if (orderCopyCompleted) {
        currentOrder = await processOrderCopyCompleted(
          orderCopyCompleted.target_order,
          orderCopyCompleted.source_order
        )
      } else {
        showNotification({
          message: t('cart.orderCopyFailed', { message: 'Order Copy failed' }),
          variant: 'danger'
        })

        Sentry.setContext('orderCopyCompleted', {
          orderCopyCompleted: orderCopyCompleted
        })

        // throw an error to catch it in the catch block
        throw new Error('Order Copy failed')
      }
    } catch (error) {
      Sentry.setContext('order', {
        order: currentOrder
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('handleOrderCopyUseCart')
        scope.setTag('order_number', currentOrder?.number)
        scope.setTag('wings_process', 'order_approve')
        return scope
      })

      showNotification({
        message: t('cart.orderCopyFailed', { message: error.message }),
        variant: 'danger'
      })
    } finally {
      // if currentOrder is placed put it to editing
      if (currentOrder.status === 'placed') {
        currentOrder = await putCartToEditing(currentOrder)
      }

      // if currentOrder is cancelled, remove it from the cart
      if (currentOrder.status === 'cancelled') {
        showNotification({
          message: t('cart.orderCopyFailed', { message: 'Order already cancelled' }),
          variant: 'danger'
        })
        // remove the order from the cart
        resetCartState()
      } else {
        customerStore.setCurrentCart(currentOrder)
      }
      uiStore.orderUpdateOverlay = false
    }
  }

  async function isOrderCopyCompleted(orderId) {
    const orderCopy = await cl.order_copies.retrieve(orderId, {
      include: ['target_order', 'source_order.attachments']
    })

    if (orderCopy.status === 'completed') {
      // show message cart refreshed
      showNotification({
        message: t('cart.orderCopyCompleted'),
        variant: 'success'
      })

      // if source order is pending or draft, archive it
      if (['draft', 'pending'].includes(orderCopy.source_order?.status)) {
        await cl.orders.update({
          id: orderCopy.source_order.id,
          _archive: true
        })
      }

      return orderCopy
    } else if (orderCopy.status === 'failed') {
      // the error can look like:
      // {
      //   "errors_log": {
      //     "cancel_source": {
      //       "status": [
      //         "transition is not permitted"
      //       ]
      //     }
      //   }
      // }
      const errors = orderCopy?.errors_log
      let message = null
      for (const key in errors) {
        if (Object.hasOwnProperty.call(errors, key) && errors[key].status) {
          message += `<b>${key}</b>: ${errors[key].status.join(', ')}<br />`
        }
      }

      if (!message) {
        try {
          message = JSON.stringify(errors)
        } catch (error) {
          console.error(orderCopy?.errors_log)
          message = 'Order Copy failed'
        }
      }

      showNotification({
        message: t('cart.orderCopyFailed', { message: message }),
        variant: 'danger'
      })

      // if target order is created but the order copy failed, archive the target order -> the target order can't be used
      if (orderCopy.target_order) {
        await cl.orders.update({
          id: orderCopy.target_order.id,
          _archive: true
        })
      }

      Sentry.setContext('orderCopy', {
        orderCopy: {
          id: orderCopy.id,
          status: orderCopy.status,
          errors: message,
          target_order: {
            id: orderCopy.target_order.id,
            number: orderCopy.target_order.number,
            stat: orderCopy.target_order.status
          }
        }
      })

      throw new Error('Order Copy failed')
    } else {
      return false
    }
  }

  async function setPaymentMethod() {
    try {
      let order = await cl.orders.retrieve(currentCart.value.id, {
        include: ['available_payment_methods', 'payment_source', 'market'],
        fields: {
          orders: [
            'available_payment_methods',
            'payment_source',
            'market',
            'status',
            'total_amount_cents'
          ]
        }
      })

      // only set payment method if order is not in placed or editing status
      if (['placed', 'editing'].includes(order.status)) return

      // if order already has a payment method, remove it
      if (order.payment_source) return

      // add payment source if total amount is greater than 0
      if (order.total_amount_cents <= 0) return

      const paymentMethod = order.available_payment_methods.find(
        (method) => method.payment_source_type === 'external_payments'
      )

      if (order.market.id !== marketId.value) {
        const orderData = {
          id: currentCart.value.id,
          payment_method: cl.payment_methods.relationship(paymentMethod.id),
          market: cl.markets.relationship(marketId.value)
        }

        order = await cl.orders.update(orderData)
      } else {
        const orderData = {
          id: order.id,
          payment_method: cl.payment_methods.relationship(paymentMethod.id)
        }

        order = await cl.orders.update(orderData)
      }

      if (!order?.payment_source?.payment_source_token) {
        await cl[paymentMethod.payment_source_type].create({
          order: cl.orders.relationship(currentCart.value.id),
          payment_source_token: PAYMENT_SOURCE_TOKEN
        })
      }
    } catch (error) {
      showNotification({
        message: 'setPaymentMethod: ' + getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  /**
   * Resumes an existing pending cart "in place": loads the cart's customer (with
   * package-deal / address context) and opens the cart drawer without navigating.
   * The passed `cart` must already carry the full cart includes (line_items.item,
   * line_item_options.sku_option, market, addresses, tags, attachments) so it can be
   * used directly as the current cart — mirror getCarts() in CustomerSearch.vue.
   *
   * @param {Object} cart - The pending cart order to open.
   */
  async function openCart(cart) {
    try {
      const customer = await loadCustomerById(cart.customer.id)
      if (!customer) {
        showNotification({
          message: 'Customer could not be loaded.',
          variant: 'danger',
          icon: 'person-x'
        })
        return
      }

      // Default billing/shipping addresses (mirrors CustomerSearch loadCustomer)
      const billingAddress = customer?.customer_addresses?.filter(
        (item) => item.address.metadata?.is_default_billing
      )
      const shippingAddress = customer?.customer_addresses?.filter(
        (item) => item.address.metadata?.is_default_shipping
      )

      if (billingAddress?.length) {
        cartStore.setBillingAddress(billingAddress[0].address)
      } else if (customer?.customer_addresses?.length > 0) {
        cartStore.setBillingAddress(customer.customer_addresses[0].address)
      } else {
        cartStore.setBillingAddress(null)
      }

      if (shippingAddress?.length) {
        cartStore.setShippingAddress(shippingAddress[0].address)
      } else if (customer?.customer_addresses?.length > 0) {
        cartStore.setShippingAddress(customer.customer_addresses[0].address)
      } else {
        cartStore.setShippingAddress(null)
      }

      const orderForPackageDeal = await getCustomerLastOrder(
        customer.id,
        deviceStore.settings?.country?.toUpperCase()
      )

      customerStore.setGuestAccount({})
      customerStore.setCustomer(customer)
      customerStore.setCustomerAddress(null)
      customerStore.setCustomerLastOrder(orderForPackageDeal)
      customerStore.setCurrentCart(cart)
      customerStore.isCustomerSelected = true
      cartStore.shippingAddressSetOnCart = false

      uiStore.showCart = true
    } catch (error) {
      showNotification({
        message: 'openCart: ' + getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  return {
    createCart,
    archiveCart,
    cartUpdate,
    deleteLineItem,
    removeInsurance,
    addLineItem,
    setImmediateHandoverOnLineItem,
    getAfterSalesGiftcard,
    cartLineItems,
    cartCaseLineItems,
    cartHasInsuranceTag,
    cartHasAfterSalesGiftCard,
    cartHasAfterSalesIntoleranceGiftCard,
    cartHasAfterSalesInsuranceGiftCard,
    cartHasIntoleranceTag,
    cartDeductibleAmount,
    cartHasGiftCardWithMinimumAmount,
    expectedDeliverySlips,
    isHandedOverDirectly,
    allGlassesAreHandedOverDirectly,
    hasAnyHandedOverItem,
    addAdjustmentAmount,
    hasRoundingAdjustment,
    addGiftcardAttachmentToOrder,
    removeGiftcardAttachmentFromOrder,
    getGiftCard,
    getGiftcardByCodeForOrder,
    getCurrentItemReference,
    createOrderCopy,
    handleCartPlaceErrors,
    setPaymentMethod,
    manuallyCopyOrder,
    handleApprove,
    hasInProgressPayment,
    openCart
  }
}
